import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useFormState, useWatch } from "react-hook-form";
import type { HydratedImage } from "../generated/api/types.gen";
import {
  type RegenerationFormValues,
  regenerateImages,
} from "../lib/regeneration";
import { useAuthRedirect } from "./useAuthRedirect";

export type { RegenerationFormValues };

interface UseRegenerationProps {
  refinementImage: HydratedImage | null;
  onImageUpdate: (image: HydratedImage) => void;
  formMethods: UseFormReturn<RegenerationFormValues>;
}

export function useRegeneration({
  refinementImage,
  onImageUpdate,
  formMethods,
}: UseRegenerationProps) {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [isRegenerating, setIsRegenerating] = useState(false);
  const { control } = formMethods;
  const promptText = useWatch({ control, name: "prompt" }) ?? "";
  const { dirtyFields } = useFormState({ control, name: "prompt" });
  const promptDirty = Boolean(dirtyFields.prompt);
  const sessionId = refinementImage?.session_id;
  const originalPrompt = refinementImage?.prompt || "";
  const redirectToSignIn = useAuthRedirect();

  // handleSubmit runs registered field validators (e.g. blank-if-originally-set)
  // before starting the regeneration stream.
  const handleRegenerateImages = formMethods.handleSubmit(() =>
    regenerateImages({
      input: {
        refinementImage,
        promptDirty,
        promptText,
        originalPrompt,
        sessionId,
      },
      form: {
        formMethods,
        onImageUpdate,
        setIsRegenerating,
      },
      deps: {
        queryClient,
        router,
        navigate,
        redirectToSignIn,
      },
    }),
  );

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    formMethods.setValue("prompt", e.target.value, { shouldDirty: true });
  };

  const handleParameterChange = () => {
    formMethods.resetField("prompt");
  };

  return {
    isRegenerating,
    promptText,
    promptDirty,
    handleRegenerateImages,
    handlePromptChange,
    handleParameterChange,
  };
}
